import axios, {
  AxiosError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from 'axios';

const httpClient = axios.create();

type GetAccessTokenSilently = () => Promise<string | undefined>;

// Adds access tokens in all API requests
export const addAccessTokenInterceptor = (
  getAccessTokenSilently: GetAccessTokenSilently
) => {
  httpClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await getAccessTokenSilently();
      if (token) {
        config.headers = config.headers || new AxiosHeaders();
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

/**
 * A 503 from the BFF means the platform is in maintenance mode. That is app
 * state rather than a route, so it is exposed as a store the React tree
 * subscribes to (see useMaintenanceMode) instead of a URL — which also means a
 * page reload is what takes the user back into the app once the BFF recovers.
 */
let maintenanceMode = false;
const maintenanceModeListeners = new Set<() => void>();

export const subscribeToMaintenanceMode = (listener: () => void) => {
  maintenanceModeListeners.add(listener);

  return () => {
    maintenanceModeListeners.delete(listener);
  };
};

export const getMaintenanceMode = (): boolean => maintenanceMode;

// Exposed for tests; the app only ever enters maintenance mode via a 503.
export const resetMaintenanceMode = () => {
  maintenanceMode = false;
};

export const isMaintenanceModeError = (error: AxiosError): boolean =>
  error.response?.status === 503;

httpClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Latching: once the platform reports maintenance we stay there until the
    // page is reloaded. Clearing on the next successful response would flap the
    // whole app tree in and out if only some endpoints were failing.
    if (isMaintenanceModeError(error) && !maintenanceMode) {
      maintenanceMode = true;
      maintenanceModeListeners.forEach((listener) => listener());
    }

    return Promise.reject(error);
  }
);

export default httpClient;
